import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RotateCcw } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const payStatus = {
  paid: ["Payé", "text-[#009246] border-[#009246]/40"],
  pending: ["En attente", "text-yellow-400 border-yellow-400/30"],
  refunded: ["Remboursé", "text-[#ff6b81] border-[#C8102E]/40"],
  failed: ["Échoué", "text-gray-400 border-gray-600"],
  expired: ["Expiré", "text-gray-400 border-gray-600"],
};

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveManual = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/payments/manual", { ...form, amount: Number(form.amount) });
      toast.success(`Paiement enregistré — ${data.ritas_earned} RITAS crédités`);
      setForm(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const refund = async (id) => {
    if (!window.confirm("Rembourser ce paiement ? Les RITAS correspondants seront retirés.")) return;
    try {
      await api.post(`/admin/payments/${id}/refund`);
      toast.success("Paiement remboursé, RITAS retirés");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-payments">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-elegant text-3xl font-bold">Paiements</h1>
        <button data-testid="manual-payment-btn" onClick={() => setForm({ email: "", amount: "", method: "card", note: "" })} className="btn-gold !py-2 text-sm">
          <Plus size={16} /> Enregistrer un paiement manuel
        </button>
      </div>

      {form && (
        <form onSubmit={saveManual} className="ic-card !transform-none p-6 mb-8 space-y-4" data-testid="manual-payment-form">
          <p className="text-sm text-gray-400">Enregistrez un paiement effectué au restaurant pour créditer les RITAS du client (carte = +20% bonus).</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <input data-testid="manual-form-email" required type="email" className="ic-input" placeholder="Email du client *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="manual-form-amount" required type="number" step="0.01" min="0.5" className="ic-input" placeholder="Montant € *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            <select data-testid="manual-form-method" className="ic-input" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
              <option value="card">Carte bancaire (+20% RITAS)</option>
              <option value="especes">Espèces</option>
            </select>
          </div>
          <input data-testid="manual-form-note" className="ic-input" placeholder="Note (optionnel)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <div className="flex gap-3">
            <button type="submit" data-testid="manual-form-save" className="btn-gold !py-2 text-sm">Valider le paiement</button>
            <button type="button" onClick={() => setForm(null)} className="btn-outline-gold !py-2 text-sm">Annuler</button>
          </div>
        </form>
      )}

      <div className="ic-card !transform-none overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-left">
            <th className="p-4 gold-label">Date</th><th className="p-4 gold-label">Client</th><th className="p-4 gold-label">Montant</th>
            <th className="p-4 gold-label">Méthode</th><th className="p-4 gold-label">RITAS</th><th className="p-4 gold-label">Statut</th><th className="p-4 gold-label"></th>
          </tr></thead>
          <tbody>
            {payments.map((p) => {
              const [label, cls] = payStatus[p.payment_status] || [p.payment_status, "text-gray-400 border-gray-600"];
              return (
                <tr key={p.id} className="border-b border-white/5" data-testid={`payment-row-${p.id}`}>
                  <td className="p-4 text-gray-400">{new Date(p.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="p-4">{p.user_email}</td>
                  <td className="p-4 font-medium">{p.amount.toFixed(2).replace(".", ",")} €</td>
                  <td className="p-4 text-gray-400">{p.method === "card" ? "Carte" : "Espèces"}{p.source === "stripe" ? " (en ligne)" : " (manuel)"}</td>
                  <td className="p-4 text-[#D4AF37]">{p.ritas_earned || 0}</td>
                  <td className="p-4"><span className={`text-xs border rounded-full px-3 py-1 ${cls}`}>{label}</span></td>
                  <td className="p-4">
                    {p.payment_status === "paid" && (
                      <button data-testid={`payment-refund-${p.id}`} onClick={() => refund(p.id)} className="text-xs border border-[#C8102E]/40 text-[#ff6b81] rounded-full px-3 py-1 flex items-center gap-1">
                        <RotateCcw size={12} /> Rembourser
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {payments.length === 0 && <p className="p-6 text-gray-500">Aucun paiement pour le moment.</p>}
      </div>
    </div>
  );
}
